import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Link,
    Typography
} from '@mui/material';

import { websiteUrl } from '../../env';
import { formatExternalUrl } from '../../utils/parsing';
import {
    COOKIE_BANNER_OPEN_EVENT,
    COOKIE_CONSENT_NAME,
    isGtmEnabled,
    readCookieValue,
    setCookieConsent
} from '../../utils/analytics';
import { COLOR_BLUE, COLOR_TEAL, COLOR_WHITE } from '../../theme';

export default function CookieBanner() {
    const [ready, setReady] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!isGtmEnabled()) return undefined;

        const existing = readCookieValue(document.cookie, COOKIE_CONSENT_NAME);
        setOpen(!existing);
        setReady(true);

        function onOpen() {
            setOpen(true);
        }
        window.addEventListener(COOKIE_BANNER_OPEN_EVENT, onOpen);
        return () => window.removeEventListener(COOKIE_BANNER_OPEN_EVENT, onOpen);
    }, []);

    if (!isGtmEnabled() || !ready || !open) return null;

    function choose(accepted: boolean) {
        setCookieConsent(accepted);
        setOpen(false);
    }

    const privacyHref = formatExternalUrl(`${String(websiteUrl).replace(/\/$/, '')}/integritet`);

    return (
        <Box
            role="dialog"
            aria-label="Cookies"
            sx={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2100,
                bgcolor: COLOR_BLUE,
                color: COLOR_WHITE,
                px: 2,
                py: 2,
                boxShadow: '0 -4px 32px rgba(0,0,0,0.58)'
            }}
        >
            <Box
                sx={{
                    maxWidth: 960,
                    mx: 'auto',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 2
                }}
            >
                <Typography
                    sx={{
                        flex: '1 1 280px',
                        fontSize: 14,
                        lineHeight: 1.5,
                        color: 'rgba(255,255,255,0.88)'
                    }}
                >
                    Vi använder cookies för statistik och marknadsföring. Välj Acceptera för att tillåta det, eller Bara
                    nödvändiga för att fortsätta utan spårning.
                    {' '}
                    <Link href={privacyHref} color="inherit" underline="always" fontWeight={600}>
                        Integritetspolicy
                    </Link>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                    <Button
                        type="button"
                        variant="contained"
                        onClick={() => choose(true)}
                        sx={{
                            height: 44,
                            px: 2.5,
                            bgcolor: COLOR_TEAL,
                            color: COLOR_WHITE,
                            '&:hover': { bgcolor: COLOR_TEAL, opacity: 0.9 }
                        }}
                    >
                        Acceptera
                    </Button>
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={() => choose(false)}
                        sx={{
                            height: 44,
                            px: 2.5,
                            color: COLOR_WHITE,
                            borderColor: 'rgba(255,255,255,0.55)',
                            '&:hover': { borderColor: COLOR_WHITE, bgcolor: 'transparent' }
                        }}
                    >
                        Bara nödvändiga
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
